import React, { useState, useRef, useEffect } from 'react';
import '../estilos/Recepcion.css';
import { obtenerActividadesKpi, calcularTerminoEstimado } from '../../servicios/programaService';

const mesesCortos = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

function formatearFecha(fechaStr) {
  if (!fechaStr) return '—';
  const partes = fechaStr.split('-');
  if (partes.length !== 3) return fechaStr;
  const dia = partes[2];
  const mes = mesesCortos[parseInt(partes[1], 10) - 1] || partes[1];
  const anio = partes[0];
  return `${dia}/${mes}/${anio}`;
}

function getColorFecha(fechaStr) {
  if (!fechaStr) return null;
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const fecha = new Date(fechaStr + 'T12:00:00');
  fecha.setHours(0, 0, 0, 0);
  const diffMs = fecha - hoy;
  const diasRestantes = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  if (diasRestantes <= 0) {
    return { bg: '#1a1a1a', color: '#ffffff', border: '#1a1a1a', label: diasRestantes === 0 ? 'Vence hoy' : `Vencido hace ${Math.abs(diasRestantes)} día${Math.abs(diasRestantes) !== 1 ? 's' : ''}` };
  }
  if (diasRestantes <= 12) {
    return { bg: '#fef2f2', color: '#dc2626', border: '#fca5a5', label: `Crítico · ${diasRestantes} día${diasRestantes !== 1 ? 's' : ''}` };
  }
  if (diasRestantes <= 15) {
    return { bg: '#fff7ed', color: '#ea580c', border: '#fdba74', label: `Alto · ${diasRestantes} días` };
  }
  if (diasRestantes <= 20) {
    return { bg: '#fefce8', color: '#ca8a04', border: '#fcd34d', label: `Medio · ${diasRestantes} días` };
  }
  if (diasRestantes <= 25) {
    return { bg: '#f0fdf4', color: '#16a34a', border: '#86efac', label: `Bajo · ${diasRestantes} días` };
  }
  return null;
}

function agregarMesesAFecha(fecha, meses) {
  if (!fecha || !meses) return fecha;
  const d = new Date(fecha);
  d.setMonth(d.getMonth() + parseInt(meses));
  return d;
}

const RecepcionObra = ({
  certificadosObra = [],
  mesesRecepcionObra = { mesesContractual: 0, mesesProgramado: 0 },
  fechasRecepcion = {},
  mesesRecepcion = 0,
  onActualizarMesesRecepcionObra,
  onCrearCertificadoObra,
  onAgregarDocumentoObra,
  onActualizarDocumentoObra,
  onEliminarDocumentoObra,
  onToggleDocumentoCompletadoObra,
}) => {
  const fileInputRefs = useRef({});
  const debounceTimers = useRef({});
  const redistribuidaRef = useRef(false);

  const [mostrarFormAgregarObra, setMostrarFormAgregarObra] = useState(false);
  const [nuevoDocNombreObra, setNuevoDocNombreObra] = useState('');
  const [mesesObraLocal, setMesesObraLocal] = useState(mesesRecepcionObra.mesesProgramado || 0);
  const [mesesObraGuardando, setMesesObraGuardando] = useState(false);
  const [terminoEstimado, setTerminoEstimado] = useState(null);

  useEffect(() => { setMesesObraLocal(mesesRecepcionObra.mesesProgramado || 0); }, [mesesRecepcionObra.mesesProgramado]);

  // Calcula el término estimado desde Programa (para la fecha base) y, si corresponde,
  // redistribuye los documentos de obra una vez contra esa fecha — independiente de si
  // el usuario visitó antes la pestaña Recepcion.
  useEffect(() => {
    obtenerActividadesKpi().then(acts => {
      const estimado = calcularTerminoEstimado(acts);
      setTerminoEstimado(estimado);

      if (estimado && !redistribuidaRef.current && onActualizarMesesRecepcionObra) {
        redistribuidaRef.current = true;
        onActualizarMesesRecepcionObra(mesesRecepcionObra.mesesContractual, mesesRecepcionObra.mesesProgramado);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const actualizarCampoDocObraConDebounce = (certId, docId, campo, valor) => {
    const key = `obra-doc-${certId}-${docId}-${campo}`;
    if (debounceTimers.current[key]) clearTimeout(debounceTimers.current[key]);
    debounceTimers.current[key] = setTimeout(() => {
      onActualizarDocumentoObra(certId, docId, { [campo]: valor }).catch(err =>
        console.error('Error al guardar campo documento obra:', err)
      );
    }, 800);
  };

  const eliminarDocumentoObra = async (certId, docId) => {
    try {
      await onEliminarDocumentoObra(certId, docId);
    } catch (error) {
      console.error('Error al eliminar documento de obra:', error);
    }
  };

  const agregarArchivosObra = (certId, docId, files) => {
    const cert = certificadosObra.find(c => c.id === certId);
    if (!cert) return;
    const doc = cert.documentos.find(d => d.id === docId);
    if (!doc) return;
    const nuevosArchivos = Array.from(files).map(file => {
      const reader = new FileReader();
      return new Promise((resolve) => {
        reader.onload = () => {
          resolve({
            nombre: file.name,
            archivoBase64: reader.result.split(',')[1],
            mimeType: file.type,
            tamano: file.size,
            fechaSubida: new Date()
          });
        };
        reader.readAsDataURL(file);
      });
    });
    Promise.all(nuevosArchivos).then(archivosConvertidos => {
      const archivosActualizados = [...(doc.archivos || []), ...archivosConvertidos];
      onActualizarDocumentoObra(certId, docId, { archivos: archivosActualizados }).catch(err =>
        console.error('Error al subir archivos obra:', err)
      );
    });
  };

  const eliminarArchivoObra = (certId, docId, archivoIdx) => {
    const cert = certificadosObra.find(c => c.id === certId);
    if (!cert) return;
    const doc = cert.documentos.find(d => d.id === docId);
    if (!doc) return;
    const archivosActualizados = doc.archivos.filter((_, i) => i !== archivoIdx);
    onActualizarDocumentoObra(certId, docId, { archivos: archivosActualizados }).catch(err =>
      console.error('Error al eliminar archivo obra:', err)
    );
  };

  const totalDocumentosObra = certificadosObra.reduce((sum, c) => sum + (c.documentos?.length || 0), 0);

  const guardarMesesObra = async () => {
    if (!onActualizarMesesRecepcionObra) return;
    setMesesObraGuardando(true);
    try {
      await onActualizarMesesRecepcionObra(mesesRecepcionObra.mesesContractual, mesesObraLocal);
    } catch {
      // error manejado en el handler
    } finally {
      setMesesObraGuardando(false);
    }
  };

  // Aplanar todos los docs de todas las partidas de obra con referencia a su certId
  const todosDocObra = certificadosObra.flatMap(cert =>
    (cert.documentos || []).map(doc => ({ ...doc, _certId: cert.id }))
  );
  const primerId = certificadosObra[0]?.id || null;

  const agregarDoc = async () => {
    if (!nuevoDocNombreObra.trim()) return;
    let certId = primerId;
    if (!certId) {
      // Crear partida implícita si no existe ninguna
      const nueva = await onCrearCertificadoObra({ nombrePartida: 'Recepción de Obra' });
      certId = nueva?.id || nueva?._id;
    }
    if (certId) {
      await onAgregarDocumentoObra(certId, { nombre: nuevoDocNombreObra.trim() });
      setNuevoDocNombreObra('');
      setMostrarFormAgregarObra(false);
    }
  };

  const addMeses = (fechaStr, meses) => {
    if (!fechaStr) return null;
    const d = new Date(fechaStr + 'T12:00:00');
    d.setMonth(d.getMonth() + (parseInt(meses) || 0));
    return d.toISOString().split('T')[0];
  };
  // Base obra programada = terminoEstimado + mesesRecepcion (la "fecha usada en Fecha Programa")
  const fechaCertStr = terminoEstimado
    ? agregarMesesAFecha(terminoEstimado, mesesRecepcion)
        .toLocaleDateString('en-CA', { timeZone: 'America/Santiago' })
    : (fechasRecepcion?.fechaRecepcionEsperada || '');
  const baseProgramada = fechaCertStr;
  const fechaRecepcionProgramada = addMeses(baseProgramada, mesesObraLocal);

  return (
    <div className="tab-content">
      <section className="recepcion-placeholder-section">
        <div className="recepcion-header-bar">
          <span className="recepcion-badge">RECEPCION</span>
          <span className="recepcion-header-title">Recepción Obra</span>
          <span style={{ marginLeft: 'auto', fontSize: '13px', color: '#6b7280' }}>
            {certificadosObra.length} partida{certificadosObra.length !== 1 ? 's' : ''} · {totalDocumentosObra} documento{totalDocumentosObra !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Banner Recepción de Obra */}
        <div className="recepcion-banner">
          <div className="recepcion-fields">
            {/* Fila Programada */}
            <div className="recepcion-field recepcion-field-programada">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <span className="recepcion-fecha-casilla">
                  {baseProgramada ? formatearFecha(baseProgramada) : '—'}
                </span>
                <span style={{ fontSize: '13px', color: '#6b7280' }}>+</span>
                <input
                  type="number"
                  min="0"
                  max="120"
                  className="recepcion-input"
                  style={{ borderColor: '#9ca3af', color: '#111827', width: '70px' }}
                  value={mesesObraLocal}
                  onChange={(e) => setMesesObraLocal(parseInt(e.target.value) || 0)}
                  onKeyDown={(e) => { if (e.key === 'Enter') guardarMesesObra(); }}
                />
                <span style={{ fontSize: '13px', color: '#6b7280' }}>meses</span>
                <button
                  className="recepcion-form-submit"
                  style={{ padding: '5px 14px', fontSize: '12px' }}
                  onClick={guardarMesesObra}
                  disabled={mesesObraGuardando}
                >
                  {mesesObraGuardando ? 'Guardando…' : 'Guardar'}
                </button>
                {fechaRecepcionProgramada && (
                  <>
                    <span style={{ fontSize: '13px', color: '#6b7280' }}>→</span>
                    <span className="recepcion-fecha-casilla">
                      {formatearFecha(fechaRecepcionProgramada)}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Barra de acciones */}
        <div className="recepcion-actions-row">
          <button
            className={`recepcion-action-btn ${mostrarFormAgregarObra ? 'recepcion-action-active' : 'recepcion-action-green'}`}
            onClick={() => setMostrarFormAgregarObra(v => !v)}
          >
            {mostrarFormAgregarObra ? 'Cancelar' : '+ Agregar Documento'}
          </button>
        </div>

        {/* Formulario rápido agregar documento */}
        {mostrarFormAgregarObra && (
          <form
            className="recepcion-add-doc-form"
            style={{ margin: '0 0 12px', padding: '10px 16px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb', display: 'flex', gap: '8px', alignItems: 'center' }}
            onSubmit={(e) => { e.preventDefault(); agregarDoc(); }}
          >
            <input
              type="text"
              className="recepcion-input"
              placeholder="Nombre del documento..."
              value={nuevoDocNombreObra}
              onChange={(e) => setNuevoDocNombreObra(e.target.value)}
              autoFocus
              style={{ flex: 1 }}
            />
            <button type="submit" className="recepcion-btn-add-doc-ok" disabled={!nuevoDocNombreObra.trim()}>✓ Agregar</button>
            <button type="button" className="recepcion-btn-add-doc-cancel" onClick={() => { setMostrarFormAgregarObra(false); setNuevoDocNombreObra(''); }}>✕</button>
          </form>
        )}

        {/* Tabla plana de documentos */}
        <div className="recepcion-table-wrapper" style={{ borderColor: '#e5e7eb' }}>
          <table className="recepcion-docs-table">
            <thead>
              <tr style={{ background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)' }}>
                <th style={{ width: '30px', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>#</th>
                <th style={{ width: '36px', textAlign: 'center', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>✓</th>
                <th className="recepcion-th-documento" style={{ color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Documento</th>
                <th className="recepcion-th-fecha" style={{ color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Fecha Programa</th>
                <th className="recepcion-th-archivos" style={{ color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Archivos</th>
                <th className="recepcion-th-observaciones" style={{ color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Observaciones</th>
                <th style={{ width: '40px', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Acc.</th>
              </tr>
            </thead>
            <tbody>
              {todosDocObra.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: '32px', color: '#9ca3af', fontSize: '13px' }}>
                    No hay documentos. Usa el botón Agregar Documento para comenzar.
                  </td>
                </tr>
              ) : (
                todosDocObra.map((doc, docIdx) => {
                  const alertaEsperada = getColorFecha(doc.fechaEsperada);
                  const fileInputKeyObra = `obra-flat-${doc._certId}-${doc.id}`;
                  return (
                    <tr key={`${doc._certId}-${doc.id}`} className={`recepcion-doc-row${doc.completado ? ' recepcion-doc-completado' : ''}`}>
                      <td style={{ textAlign: 'center', color: '#6b7280', fontSize: '12px' }}>{docIdx + 1}</td>
                      <td style={{ textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={doc.completado || false}
                          onChange={() => onToggleDocumentoCompletadoObra(doc._certId, doc.id)}
                          style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#374151' }}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          className="recepcion-input recepcion-input-documento"
                          placeholder="Nombre del documento..."
                          defaultValue={doc.nombre}
                          onBlur={(e) => { if (e.target.value !== doc.nombre) onActualizarDocumentoObra(doc._certId, doc.id, { nombre: e.target.value }); }}
                          onChange={(e) => actualizarCampoDocObraConDebounce(doc._certId, doc.id, 'nombre', e.target.value)}
                        />
                      </td>
                      <td>
                        <span className="recepcion-fecha-texto" title={alertaEsperada?.label || ''} style={alertaEsperada ? { background: alertaEsperada.bg, color: alertaEsperada.color, border: `1px solid ${alertaEsperada.border}` } : {}}>
                          {formatearFecha(doc.fechaEsperada)}
                        </span>
                      </td>
                      <td className="recepcion-td-archivos">
                        <div className="recepcion-archivos-container">
                          {doc.archivos.length > 0 && (
                            <div className="recepcion-archivos-lista">
                              {doc.archivos.map((arch, archIdx) => (
                                <div key={archIdx} className="recepcion-archivo-item">
                                  <span className="recepcion-archivo-titulo" title={arch.nombre}>{arch.nombre}</span>
                                  <div className="recepcion-archivo-acciones">
                                    {arch.archivoBase64 && (
                                      <a href={`data:${arch.mimeType || 'application/octet-stream'};base64,${arch.archivoBase64}`} target="_blank" rel="noopener noreferrer" className="recepcion-archivo-ver" title="Ver archivo">ver</a>
                                    )}
                                    <button className="recepcion-archivo-eliminar" onClick={() => eliminarArchivoObra(doc._certId, doc.id, archIdx)} title="Eliminar archivo">✕</button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          <input
                            type="file"
                            multiple
                            ref={el => fileInputRefs.current[fileInputKeyObra] = el}
                            style={{ display: 'none' }}
                            onChange={(e) => { agregarArchivosObra(doc._certId, doc.id, e.target.files); e.target.value = ''; }}
                          />
                          <button className="recepcion-btn-subir-archivo" onClick={() => fileInputRefs.current[fileInputKeyObra]?.click()} title="Subir archivos">Subir</button>
                        </div>
                      </td>
                      <td>
                        <textarea
                          className="recepcion-input recepcion-observaciones-textarea"
                          placeholder="Observaciones..."
                          defaultValue={doc.observaciones}
                          rows={2}
                          onBlur={(e) => { if (e.target.value !== doc.observaciones) onActualizarDocumentoObra(doc._certId, doc.id, { observaciones: e.target.value }); }}
                          onChange={(e) => actualizarCampoDocObraConDebounce(doc._certId, doc.id, 'observaciones', e.target.value)}
                        />
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <button className="recepcion-btn-eliminar" onClick={() => eliminarDocumentoObra(doc._certId, doc.id)} title="Eliminar documento">✕</button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default RecepcionObra;
